import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import { AttributeBase } from '@approot/models/attribute-base';
import { isBuyPosition, isSellPosition, Utilities } from '@approot/utilities';
import { Throttle } from 'lodash-decorators';
import { getAllOptions } from '../leg/utilities/leg-attribute-utilities';

@Component({
  selector: 'leg-attribute',
  templateUrl: './dynamic-leg-attribute.component.html',
  styleUrls: ['./dynamic-leg-attribute.component.scss']
})
export class DynamicLegAttributeComponent implements OnInit, AfterViewInit {
  @Input() public attribute: AttributeBase<string>;
  @Input() public form: FormGroup;
  @Output() public onDropdownChange = new EventEmitter();
  @Output() public onButtonClick = new EventEmitter();

  public isCross = false;
  public listFieldFocused: string;
  public bidAskListError = '';
  public fieldTouched = false;

  @ViewChild('formElement', { static: false, read: ElementRef })
  formElement: ElementRef;

  nestedHtmlInputElement = null;

  ngAfterViewInit() {
    this.findNestedInputElement(this.formElement.nativeElement);
    if (
      this.attribute.selectFirst &&
      this.attribute.options.length &&
      this.form.get(this.attribute.key) &&
      !this.form.get(this.attribute.key)?.value
    ) {
      this.form.get(this.attribute.key).setValue(this.attribute.options[0]);
    }
  }

  emitOnButtonClicked() {
    console.log('1 button clicked');

    this.onButtonClick.emit();
  }

  findNestedInputElement(element: HTMLElement) {
    if (!element) return;

    const inputs = element.querySelectorAll('input');
    inputs.forEach(input => {
      this.nestedHtmlInputElement = input;
    });

    if (!this.nestedHtmlInputElement) {
      // Recursively call the function for all child elements
      const children = element.children;
      for (let i = 0; i < children.length; i++) {
        this.findNestedInputElement(children[i] as HTMLElement);
      }
    }
  }

  public ngOnInit(): void {
    this.dropdownSelect();
  }

  public get isValid(): boolean {
    if (this.attribute.controlType === 'bidAskList') {
      try {
        if (this.attribute.key === 'margin') {
          return true;
        }
        const attr = this.attribute.attributes;
        const bid = this.cleanNumber(attr[0].key);
        const ask = this.cleanNumber(attr[1].key);

        if (this.handleInvalidListCtrl()) {
          return false;
        }

        if (isNaN(bid) || isNaN(ask)) {
          this.bidAskListError = 'Both bid and offer must be a number';
          return false;
        }

        this.bidAskListError = 'The offer should be greater than the bid';
        return bid <= ask;
      } catch {
        this.bidAskListError = '';
        return false;
      }
    }
    return !!(
      this.form.controls[this.attribute.key]?.disabled ||
      this.form.controls[this.attribute.key]?.valid
    );
  }

  @Throttle(500)
  public get isValidTmpl(): boolean {
    return this.isValid;
  }

  @Throttle(500)
  public isSearchable(key: string) {
    switch (key) {
      case 'cross':
      case 'alternativeCurrency':
      case 'secondCross':
        return true;
      default:
        return false;
    }
  }

  public dropdownChange(event: { value: any; userSelect: boolean }) {
    // sets the color of an element
    let textSelected = event.value;

    if (event.userSelect) {
      this.handleOppositePosition();
    }

    if (isBuyPosition(textSelected)) {
      this.attribute.textColor = 'var(--positive-green)';
    } else if (isSellPosition(textSelected)) {
      this.attribute.textColor = 'var(--negative-red)';
    } else {
      this.attribute.textColor = 'inherit';
    }
    if (event.userSelect) {
      this.onDropdownChange.emit();
    }
  }

  public onFocus(field: string): void {
    this.listFieldFocused = field;
    this.fieldTouched = true;
  }

  public onBlur(): void {
    this.listFieldFocused = null;
  }

  /** PRIVATE *************************************************************/

  // If these attributes exist, boolean is true which allows user to select dropdown item on tab
  private dropdownSelect() {
    if (
      this.attribute &&
      (this.attribute.key === 'cross' || this.attribute.key === 'secondCross')
    ) {
      this.isCross = true;
    } else {
      this.isCross = false;
    }
  }

  private handleOppositePosition() {
    this.switchOtherAttributesValue('position');
    this.switchOtherAttributesValue('optionType');
    this.switchOtherAttributesValue('direction');
    this.switchOtherAttributesValue('currency');
  }

  private switchOtherAttributesValue(attributeName: string) {
    // switches the other dropdown attribute value to be the opposite of this attributes value
    const attribute1Name = attributeName + '1';
    const attribute2Name = attributeName + '2';
    const options = getAllOptions(this.attribute.options);
    if (
      this.form.get(attribute1Name) &&
      this.form.get(attribute2Name) &&
      options.length > 1 &&
      this.form.get(this.attribute.key).value &&
      (this.attribute.key === attribute1Name ||
        this.attribute.key === attribute2Name)
    ) {
      this.form
        .get(
          this.attribute.key === attribute1Name
            ? attribute2Name
            : attribute1Name
        )
        .setValue(
          options[
            this.form.get(this.attribute.key).value === options[0] ? 1 : 0
          ]
        );
    }
  }

  private cleanNumber(key: string): number {
    return Utilities.formattedNumberToNumber(
      (this.form.get(key).value as string).replace('%', '')
    );
  }

  private isInvalidAndDirty(field: string): boolean {
    const ctrl = this.form.get(field);
    return !ctrl.valid && ctrl.dirty;
  }

  private possibleErrors(field: string): string {
    const errors = this.form.get(field).errors?.invalidEntry?.errorsRelatedTo;
    return errors
      ?.map(error => {
        const errorSide = field.toLowerCase().includes('bid') ? 'bid' : 'offer';
        if (
          error === 'percentage' &&
          this.listFieldFocused !== field &&
          this.isInvalidAndDirty(field)
        ) {
          // ignore percentage formatting issue as this is added on blur
          return `${errorSide} must be be a percent`;
        }

        if (error === 'positiveNumber') {
          return `${errorSide} must be a positive value`;
        }

        return null;
      })
      .find(x => x);
  }

  private handleInvalidListCtrl(): boolean {
    const errors = [];
    this.attribute.attributes.forEach(attr => {
      const error = this.possibleErrors(attr.key);
      if (error) {
        errors.push(error);
      }
    });

    this.bidAskListError = errors.join(', ');
    return !!errors.length;
  }
}
